import * as THREE from 'three';
import { NodeObject } from '3d-force-graph';
import SpriteText from 'three-spritetext';
import { GalaxyNode, GalaxyNodeType } from '../models/galaxy-node';

interface NodeConfig {
  radius: number;
  mainColor: string;
  opacity: number;
  includeText: boolean;
  includeOutline: boolean;
  textBackgroundColor: boolean;
  includeRings?: boolean;
  ringSpacing?: number;
}

interface MaterialConfig {
  color: string;
  transparent: boolean;
  opacity: number;
  side?: THREE.Side;
}

export class GalaxyNodeRenderer {
  private static readonly NODE_CONFIGS: Record<GalaxyNodeType, NodeConfig> = {
    [GalaxyNodeType.ORIGIN]: {
      radius: 80,
      mainColor: '#ffffff',
      opacity: 0.7,
      includeText: true,
      includeOutline: true,
      textBackgroundColor: true,
    },
    [GalaxyNodeType.SPIRAL]: {
      radius: 60,
      mainColor: '#000000',
      opacity: 1.0,
      includeText: true,
      includeOutline: true,
      textBackgroundColor: true,
    },
    [GalaxyNodeType.CLUSTER]: {
      radius: 30,
      mainColor: '#333333',
      opacity: 0.7,
      includeText: true,
      includeOutline: true,
      textBackgroundColor: false,
    },
    [GalaxyNodeType.STAR]: {
      radius: 5,
      mainColor: '#333333',
      opacity: 0.7,
      includeText: true,
      includeOutline: true,
      textBackgroundColor: false,
      includeRings: true,
      ringSpacing: 2,
    },
    [GalaxyNodeType.PLANET]: {
      radius: 1,
      mainColor: '#45b7d1',
      opacity: 1.0,
      includeText: false,
      includeOutline: false,
      textBackgroundColor: false,
    },
  };

  private static readonly NODE_COLORS: Record<GalaxyNodeType, string> = {
    [GalaxyNodeType.ORIGIN]: '#ffcc00',
    [GalaxyNodeType.SPIRAL]: '#ffcc00',
    [GalaxyNodeType.CLUSTER]: '#ff6b6b',
    [GalaxyNodeType.STAR]: '#4ecdc4',
    [GalaxyNodeType.PLANET]: '#45b7d1',
  };

  static nodeAutoColorBy = (node: NodeObject): string => {
    return this.getNodeColor(node as GalaxyNode) || '#ffffff';
  };

  static nodeLabel = (node: NodeObject): string => {
    return (node as GalaxyNode).name;
  };

  static nodeThreeObject = (node: NodeObject): THREE.Object3D => {
    const galaxyNode = node as GalaxyNode;
    const config = this.NODE_CONFIGS[galaxyNode.type];

    if (!config) {
      throw new Error(`Unknown node type: ${galaxyNode.type}`);
    }

    return this.createNodeObject3D(galaxyNode, config);
  };

  private static createNodeObject3D(node: GalaxyNode, config: NodeConfig): THREE.Object3D {
    if (node.type === GalaxyNodeType.PLANET) {
      return this.createSimplePlanetObject3D(node, config);
    }

    return this.createComplexNodeObject3D(node, config);
  }

  private static createComplexNodeObject3D(node: GalaxyNode, config: NodeConfig): THREE.Object3D {
    const group = new THREE.Group();

    // Main sphere
    const mainSphere = this.createSphere(config.radius, {
      color: config.mainColor,
      transparent: true,
      opacity: config.opacity,
    });
    group.add(mainSphere);

    // Outline sphere if required
    if (config.includeOutline) {
      // Create outline sphere with emissive material for bloom effect
      const outlineGeometry = new THREE.SphereGeometry(config.radius * 1.05, 32, 32);
      const outlineMaterial = new THREE.MeshStandardMaterial({
        color: this.getNodeColor(node),
        emissive: new THREE.Color(2, 2, 2),
        emissiveIntensity: 1.0,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0.8,
      });

      const outlineSphere = new THREE.Mesh(outlineGeometry, outlineMaterial);

      // Mark outline sphere for identification
      outlineSphere.userData.isBloomObject = true;

      group.add(outlineSphere);
    }

    // Simple horizontal rings for stars
    if (node.type === GalaxyNodeType.STAR && config.includeRings && config.ringSpacing) {
      const rings = this.createSimpleRings(config.radius, config.ringSpacing, this.getNodeColor(node));
      rings.userData.isRings = true; // Mark as rings for scaling prevention
      group.add(rings);
    }

    // Text label if required
    if (config.includeText) {
      const textSprite = this.createTextSprite(node, config.radius, config.textBackgroundColor);
      group.add(textSprite);
    }

    return group;
  }

  private static createSimplePlanetObject3D(node: GalaxyNode, config: NodeConfig): THREE.Object3D {
    return this.createSphere(config.radius, {
      color: this.getNodeColor(node),
      transparent: false,
      opacity: config.opacity,
    });
  }

  private static createSphere(radius: number, materialConfig: MaterialConfig): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const material = new THREE.MeshBasicMaterial(materialConfig);
    return new THREE.Mesh(geometry, material);
  }

  private static createSimpleRings(starRadius: number, ringSpacing: number, color: string): THREE.Group {
    const ringGroup = new THREE.Group();

    // Create 4 simple rings in the horizontal plane
    for (let i = 1; i <= 4; i++) {
      const ringRadius = starRadius + i * ringSpacing;
      const ring = this.createHorizontalRing(ringRadius, color);
      ringGroup.add(ring);
    }

    return ringGroup;
  }

  private static createHorizontalRing(radius: number, color: string): THREE.Mesh {
    const innerRadius = radius - 0.3; // Very thin rings
    const outerRadius = radius + 0.3;
    const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 32);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
    });

    const ring = new THREE.Mesh(geometry, material);

    return ring;
  }

  private static createTextSprite(node: GalaxyNode, radius: number, hasBackground: boolean): SpriteText {
    const displayText = this.formatTextWithNewlines(node.name);
    const text = new SpriteText(displayText);
    text.material.depthTest = false;
    text.material.depthWrite = false;
    text.renderOrder = 999;
    text.color = this.getNodeColor(node);
    text.textHeight = radius / 3;

    if (hasBackground) {
      text.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      text.padding = 2;
      text.borderRadius = 4;
    }

    return text;
  }

  private static formatTextWithNewlines(text: string): string {
    // Split text by spaces and join with newlines
    return text.split(' ').join('\n');
  }

  static getNodeColor(node: GalaxyNode): string {
    return this.NODE_COLORS[node.type] || '#ffffff';
  }
}
